import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiGet, apiDel, API } from '../../lib/api';
import { Upload, FileText, AlertCircle, Calendar, Trash2, Download, X } from 'lucide-react';
import { toast } from 'sonner';
import DriveAttachments from '../../components/DriveAttachments';

const DOC_TYPES = [
  'Aadhaar', 'PAN', 'Passport', 'Driving License', 'Voter ID',
  'Offer Letter', 'Joining Letter', 'NDA', 'Employment Contract',
  'Education Certificate', 'Experience Letter', 'Bank Document',
  'Safety Training Cert', 'Trade Cert', 'Medical Cert', 'ID Card', 'Other'
];

const fmt = (s) => s ? new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function DocumentVault() {
  const [employees, setEmployees] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [docs, setDocs] = useState([]);
  const [alerts, setAlerts] = useState({ expired: [], expiring_soon: [], counts: {} });
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState({ doc_type: 'Aadhaar', doc_number: '', issue_date: '', expiry_date: '' });
  const [file, setFile] = useState(null);

  const loadEmployees = async () => {
    try {
      const list = await apiGet('/hr/employees');
      setEmployees(list);
      if (list.length && !selectedId) setSelectedId(list[0].id);
    } catch (err) { console.error(err); }
  };

  // ?employee={employee_id} — pre-select that employee (used by Onboarding redirect)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const empParam = searchParams.get('employee');
    if (empParam && employees.length > 0) {
      const found = employees.find(e => e.id === empParam || e.emp_id === empParam);
      if (found) {
        setSelectedId(found.id);
        setSearchParams({});
        toast.info(`Pre-selected ${found.name} — upload missing documents to unblock the onboarding task.`);
      }
    }
    // eslint-disable-next-line
  }, [employees, searchParams]);

  const loadDocs = async () => {
    if (!selectedId) return;
    try {
      const res = await apiGet(`/hr/employees/${selectedId}/documents`);
      setDocs(res.documents || []);
    } catch (err) { console.error(err); }
  };

  const loadAlerts = async () => {
    try {
      const res = await apiGet('/hr/documents/expiry-alerts?days=60');
      setAlerts(res);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { loadEmployees(); loadAlerts(); }, []);
  useEffect(() => { loadDocs(); }, [selectedId]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error('Pick a file');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const params = new URLSearchParams({
        doc_type: meta.doc_type,
        ...(meta.doc_number && { doc_number: meta.doc_number }),
        ...(meta.issue_date && { issue_date: meta.issue_date }),
        ...(meta.expiry_date && { expiry_date: meta.expiry_date }),
      });
      const res = await fetch(`${API}/hr/employees/${selectedId}/documents?${params}`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Document uploaded');
      setShowUpload(false); setFile(null);
      setMeta({ doc_type: 'Aadhaar', doc_number: '', issue_date: '', expiry_date: '' });
      loadDocs(); loadAlerts();
    } catch (err) { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete document?\n\nThis action cannot be undone.')) return;
    try { await apiDel(`/hr/documents/${id}`); toast.success('Deleted'); loadDocs(); loadAlerts(); }
    catch (err) { toast.error('Failed'); }
  };

  const download = (id) => {
    window.open(`${API}/hr/documents/${id}/download`, '_blank');
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#1F7A6E] mb-2">People & HR</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Document Vault</h1>
          <p className="text-sm text-slate-600 mt-1">KYC, certs & contracts — secure storage with expiry alerts for renewals.</p>
        </div>
      </div>

      {/* Expiry alerts banner */}
      {(alerts.counts?.expired > 0 || alerts.counts?.expiring_soon > 0) && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden mb-5" data-testid="expiry-alerts">
          <div className="px-5 py-3 bg-red-50 border-b border-red-100 flex items-center gap-3">
            <AlertCircle size={16} className="text-red-700" />
            <span className="font-display font-bold text-sm text-red-700">
              {alerts.counts.expired} expired · {alerts.counts.expiring_soon} expiring within 60 days
            </span>
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
            {[...alerts.expired, ...alerts.expiring_soon].slice(0, 8).map(d => (
              <div key={d.id} className="px-5 py-2.5 flex items-center gap-4 text-sm hover:bg-slate-50">
                <span className={`tag ${d.days_overdue !== undefined ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                  {d.days_overdue !== undefined ? `${d.days_overdue}d overdue` : `${d.days_to_expire}d left`}
                </span>
                <span className="font-display font-semibold">{d.doc_type}</span>
                <span className="text-xs text-slate-500">{d.employee_name} ({d.employee_emp_id})</span>
                <span className="text-xs text-slate-500 ml-auto">Expires {fmt(d.expiry_date)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        <aside className="bg-white border border-slate-200 rounded-sm">
          <div className="px-4 py-3 border-b border-slate-200">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500">Employees</div>
          </div>
          <ul className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
            {employees.map(emp => (
              <li key={emp.id}>
                <button
                  onClick={() => setSelectedId(emp.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 ${selectedId === emp.id ? 'bg-teal-50/60 border-l-2 border-l-[#1F7A6E]' : ''}`}
                >
                  <div className="font-display font-semibold text-sm text-[#0F172A] truncate">{emp.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{emp.emp_id}</div>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <main>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-lg">Documents</h3>
            <button onClick={() => setShowUpload(true)} className="btn-primary flex items-center gap-2" disabled={!selectedId} data-testid="upload-doc-btn">
              <Upload size={14} /> Upload
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {docs.map(d => {
              const today = new Date().toISOString().slice(0, 10);
              const overdue = d.expiry_date && d.expiry_date < today;
              const expSoon = d.expiry_date && d.expiry_date >= today && d.expiry_date < new Date(Date.now() + 60 * 86400000).toISOString().slice(0, 10);
              return (
                <div key={d.id} className={`bg-white border rounded-sm p-4 ${overdue ? 'border-red-300' : expSoon ? 'border-amber-300' : 'border-slate-200'}`} data-testid={`doc-card-${d.id}`}>
                  <div className="flex items-start gap-3">
                    <FileText size={18} className="text-[#1F7A6E] mt-1 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-sm text-[#0F172A]">{d.doc_type}</div>
                      {d.doc_number && <div className="text-xs text-slate-500 mt-0.5">{d.doc_number}</div>}
                      <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                        <Calendar size={11} />
                        <span>Issued {fmt(d.issue_date)}</span>
                        {d.expiry_date && <span className={overdue ? 'text-red-700 font-bold' : expSoon ? 'text-amber-700 font-bold' : ''}>· Expires {fmt(d.expiry_date)}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <button onClick={() => download(d.id)} className="text-slate-400 hover:text-[#1F7A6E]"><Download size={14} /></button>
                      <button onClick={() => remove(d.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              );
            })}
            {!docs.length && <div className="col-span-full p-8 text-center text-sm text-slate-500 bg-white border border-slate-200 rounded-sm">No documents uploaded yet.</div>}
          </div>

          {selectedId && (
            <div className="mt-6 bg-white border border-slate-200 rounded-sm p-4">
              <DriveAttachments
                contextType="employee"
                contextId={selectedId}
                category="Employee Documents"
                title="Bulky Files · Google Drive (Contracts, Scans, etc.)"
              />
            </div>
          )}
        </main>
      </div>

      {showUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-lg w-full">
            <div className="border-b border-slate-200 p-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">Upload Document</h2>
              <button onClick={() => setShowUpload(false)} className="text-slate-500"><X size={20} /></button>
            </div>
            <form onSubmit={upload} className="p-5 space-y-4">
              <div>
                <label className="label">Document Type *</label>
                <select value={meta.doc_type} onChange={(e) => setMeta({ ...meta, doc_type: e.target.value })} className="input" required>
                  {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Document Number</label>
                <input type="text" value={meta.doc_number} onChange={(e) => setMeta({ ...meta, doc_number: e.target.value })} className="input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="label">Issue Date</label><input type="date" value={meta.issue_date} onChange={(e) => setMeta({ ...meta, issue_date: e.target.value })} className="input" /></div>
                <div><label className="label">Expiry Date</label><input type="date" value={meta.expiry_date} onChange={(e) => setMeta({ ...meta, expiry_date: e.target.value })} className="input" /></div>
              </div>
              <div>
                <label className="label">File * (max 25 MB)</label>
                <input type="file" onChange={(e) => setFile(e.target.files[0])} className="input" required />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShowUpload(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" disabled={uploading}>{uploading ? 'Uploading…' : 'Upload'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
